/**
 * Thin wrappers over the shared `crud` registry entry for the real merchant
 * models. All reads/writes still go through @api (useQuery / useMutation).
 */
import { useQuery, useMutation } from '@/lib/useQuery';
import { firstRow } from './lib/catalog';
import { mixedWithCondition } from '@/lib/conditions';

/** List rows: { data: rows, totalCount, loading, error, reload }. */
export function useCrudList(model, body = {}, opts = {}) {
  const q = useQuery('crud', { model, action: 'list', ...body }, { ...opts, select: (r) => r });
  const res = q.data;
  return {
    ...q,
    data: res === undefined ? undefined : Array.isArray(res) ? res : (res && res.data) || [],
    totalCount: res && !Array.isArray(res) ? res.totalCount : undefined,
  };
}

/**
 * Row count. The HTTP door returns `{}` for KORM `count` (a bare number is
 * spread into the JSON body), so count via a 1-row list and its totalCount.
 */
export function useCrudCount(model, where = {}, opts = {}) {
  return useQuery('crud', { model, action: 'list', where, limit: 1, select: ['id'] }, { ...opts, select: (r) => (r && r.totalCount) || 0 });
}

/** create/update helpers returning the affected row. */
export function useCrudWrite() {
  const m = useMutation('crud');
  return {
    ...m,
    create: async (model, data) => firstRow(await m.run({ model, action: 'create', data })),
    update: async (model, id, data) => {
      // A unit's condition changes in its own request (the server refuses a mixed one).
      const mixed = model === 'InventoryUnit' ? mixedWithCondition(data) : [];
      if (mixed.length) throw new Error(`Change the condition in its own request (not with ${mixed.join(', ')}).`);
      return firstRow(await m.run({ model, action: 'update', where: { id }, data }));
    },
  };
}
